import React, { useState, useEffect, useRef } from "react";
import {
  ArrowUp,
  Play,
  Pause,
  RotateCcw,
  ArrowLeftRight,
  RefreshCw,
} from "lucide-react";

const TomAndJerrySimulation = () => {
  // 游戏基本设置
  const POOL_RADIUS = 150; // 显示半径，实际单位为10
  const SCALE_FACTOR = POOL_RADIUS / 10; // 缩放因子，将物理单位转换为像素
  const JERRY_SPEED = 0.1 * SCALE_FACTOR; // 0.1单位/秒，精确计算后从中心到边缘需要100秒
  const TOM_SPEED = 0.4 * SCALE_FACTOR; // 0.4单位/秒，是Jerry速度的4倍
  const SIMULATION_SPEED = 1; // 基础模拟速度，可以通过界面调整

  // 画布引用
  const canvasRef = useRef(null);
  const poolCenterRef = useRef({ x: 250, y: 250 });

  const [simulationSpeedFactor, setSimulationSpeedFactor] = useState(1);

  // 绕圈游泳相关状态
  const [circleSwimRadius, setCircleSwimRadius] = useState(null); // 绕圈半径
  const [circleSwimAngle, setCircleSwimAngle] = useState(0); // 绕圈当前角度

  const [captureEffect, setCaptureEffect] = useState(false);

  // 游戏状态变量
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [maxTime, setMaxTime] = useState(100);
  const [strategy, setStrategy] = useState("directedSwim");
  const [direction, setDirection] = useState(135); // 角度
  const [reactionPrecision, setReactionPrecision] = useState(5); // 方向重新计算的时间间隔（越小反应越快）
  const [lastDirectionUpdate, setLastDirectionUpdate] = useState(0); // 上次更新方向的时间
  const [circlePoint, setCirclePoint] = useState({ x: 250, y: 250 }); // 默认为圆心
  const [circleDirection, setCircleDirection] = useState("clockwise");
  const [showTrails, setShowTrails] = useState(true);
  const [showGuideLines, setShowGuideLines] = useState(false); // 默认不显示辅助连线
  const [hasEscaped, setHasEscaped] = useState(false);
  const [hasCaught, setHasCaught] = useState(false);
  const [timeStep, setTimeStep] = useState(0.1); // 固定的时间步长

  // 角色位置
  const [jerry, setJerry] = useState({ x: 250, y: 250 });
  const [tom, setTom] = useState({ x: 250, y: 250 - POOL_RADIUS });

  // 轨迹
  const [jerryTrail, setJerryTrail] = useState([]);
  const [tomTrail, setTomTrail] = useState([]);

  // 检查Jerry是否在圆心附近
  const isJerryAtCenter = () => {
    const distanceToCenter = distance(jerry, poolCenterRef.current);
    return distanceToCenter < 20; // 如果距离圆心小于20像素，认为在圆心附近
  };

  // 当选择"绕圈游泳"策略时，设置初始半径
  useEffect(() => {
    if (strategy === "circleSwim" && !isRunning) {
      // 默认围绕圆心绕圈
      setCirclePoint({
        x: poolCenterRef.current.x,
        y: poolCenterRef.current.y,
      });

      // 如果半径尚未设置，计算当前Jerry与圆心的距离作为初始半径
      const initialRadius = distance(jerry, poolCenterRef.current);
      if (initialRadius > 20) {
        // 如果Jerry不在圆心附近
        setCircleSwimRadius(initialRadius);
        // 保存当前角度作为初始角度
        const initialAngle = angleBetween(poolCenterRef.current, jerry);
        setCircleSwimAngle(initialAngle);
      } else {
        // Jerry在圆心附近，不设置半径，等待用户移动Jerry
        setCircleSwimRadius(null);
      }
    }
  }, [strategy, jerry, isRunning]);

  // 计算Tom的新位置 - 优化版本，允许调整步长
  const calculateTomPosition = (jerry, tom) => {
    // Tom是始终在池边的，所以我们需要找到离Jerry最近的点
    const tomAngleToCenter = angleBetween(poolCenterRef.current, tom);
    const jerryAngleToCenter = angleBetween(poolCenterRef.current, jerry);

    // 判断顺时针还是逆时针移动距离更短
    let angleDifference = jerryAngleToCenter - tomAngleToCenter;

    // 将角度差标准化到-PI到PI之间
    while (angleDifference > Math.PI) angleDifference -= 2 * Math.PI;
    while (angleDifference < -Math.PI) angleDifference += 2 * Math.PI;

    // 确定Tom的移动方向
    let tomMovementDirection = angleDifference > 0 ? 1 : -1;

    // 计算当前距离
    const currentDistance = distance(tom, jerry);

    // 最大步长 - 基于Tom的速度
    const maxTomAngularSpeed = (TOM_SPEED * SIMULATION_SPEED) / POOL_RADIUS;

    // 初始化最佳步长和最佳方向
    let bestAngularSpeed = maxTomAngularSpeed;
    let bestDirection = 0;
    let bestDistance = currentDistance;

    // 测试不同的步长 - 从最大步长到最小步长
    const stepSizes = [1.0, 0.75, 0.5, 0.25, 0.1, 0.05, 0.01]; // 不同步长比例

    // 尝试不同方向和步长的组合
    for (const direction of [-1, 1]) {
      // 逆时针和顺时针
      for (const stepSize of stepSizes) {
        const testAngularSpeed = maxTomAngularSpeed * stepSize;
        const testAngle = tomAngleToCenter + direction * testAngularSpeed;

        const testX =
          poolCenterRef.current.x + Math.cos(testAngle) * POOL_RADIUS;
        const testY =
          poolCenterRef.current.y + Math.sin(testAngle) * POOL_RADIUS;

        const testDistance = distance({ x: testX, y: testY }, jerry);

        // 如果找到更好的距离，更新最佳参数
        if (testDistance < bestDistance) {
          bestDistance = testDistance;
          bestDirection = direction;
          bestAngularSpeed = testAngularSpeed;
        }
      }
    }

    // 如果没有找到更好的位置，就停在原地
    if (bestDistance >= currentDistance) {
      return { x: tom.x, y: tom.y };
    }

    // 使用最佳参数计算新位置
    const newTomAngle = tomAngleToCenter + bestDirection * bestAngularSpeed;

    // 转换为直角坐标
    const newTomX =
      poolCenterRef.current.x + Math.cos(newTomAngle) * POOL_RADIUS;
    const newTomY =
      poolCenterRef.current.y + Math.sin(newTomAngle) * POOL_RADIUS;

    return { x: newTomX, y: newTomY };
  };

  // 重置游戏
  const resetSimulation = () => {
    setIsRunning(false);
    setElapsedTime(0);
    setJerry({ x: poolCenterRef.current.x, y: poolCenterRef.current.y });
    setTom({
      x: poolCenterRef.current.x,
      y: poolCenterRef.current.y - POOL_RADIUS,
    });
    setJerryTrail([]);
    setTomTrail([]);
    setHasEscaped(false);
    setHasCaught(false);
    setCircleSwimRadius(null);
    setCircleSwimAngle(0);
    setCirclePoint({ x: poolCenterRef.current.x, y: poolCenterRef.current.y });
  };

  // 计算两点之间的距离
  const distance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  // 计算两点之间的角度
  const angleBetween = (p1, p2) => {
    return Math.atan2(p2.y - p1.y, p2.x - p1.x);
  };

  // 在画布上绘制游戏
  const drawSimulation = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 获取高DPI屏幕的像素比
    const pixelRatio = window.devicePixelRatio || 1;
    const ctx = canvas.getContext("2d");

    // 设置画布尺寸以适应高DPI屏幕
    if (
      canvas.width !== 500 * pixelRatio ||
      canvas.height !== 500 * pixelRatio
    ) {
      canvas.width = 500 * pixelRatio;
      canvas.height = 500 * pixelRatio;
      canvas.style.width = "500px";
      canvas.style.height = "500px";
      ctx.scale(pixelRatio, pixelRatio);
    }

    ctx.clearRect(0, 0, 500, 500);

    // 绘制游泳池
    ctx.beginPath();
    ctx.arc(
      poolCenterRef.current.x,
      poolCenterRef.current.y,
      POOL_RADIUS,
      0,
      2 * Math.PI
    );
    ctx.fillStyle = "rgba(135, 206, 250, 0.6)";
    ctx.fill();
    ctx.strokeStyle = "rgba(0, 162, 232, 1)";
    ctx.lineWidth = 2;
    ctx.stroke();

    // 如果策略是围绕点游泳，绘制圆心点和绕圈半径
    if (strategy === "circleSwim") {
      ctx.beginPath();
      ctx.arc(circlePoint.x, circlePoint.y, 5, 0, 2 * Math.PI);
      ctx.fillStyle = "rgba(255, 0, 0, 0.5)";
      ctx.fill();

      // 绘制当前绕圈半径
      if (circleSwimRadius && circleSwimRadius > 0) {
        ctx.beginPath();
        ctx.arc(circlePoint.x, circlePoint.y, circleSwimRadius, 0, 2 * Math.PI);
        ctx.strokeStyle = "rgba(255, 0, 0, 0.2)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // 绘制辅助连线
    if (showGuideLines) {
      // 绘制泳池圆心到老鼠的连线
      ctx.beginPath();
      ctx.moveTo(poolCenterRef.current.x, poolCenterRef.current.y);
      ctx.lineTo(jerry.x, jerry.y);
      ctx.strokeStyle = "rgba(0, 100, 0, 0.6)"; // 深绿色
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // 绘制泳池圆心到猫咪的连线
      ctx.beginPath();
      ctx.moveTo(poolCenterRef.current.x, poolCenterRef.current.y);
      ctx.lineTo(tom.x, tom.y);
      ctx.strokeStyle = "rgba(200, 0, 0, 0.6)"; // 深红色
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // 计算并显示角度
      const jerryAngle =
        ((angleBetween(poolCenterRef.current, jerry) * 180) / Math.PI + 360) %
        360;
      const tomAngle =
        ((angleBetween(poolCenterRef.current, tom) * 180) / Math.PI + 360) %
        360;
      const angleDiff = (tomAngle - jerryAngle + 360) % 360;

      // 在连线中间显示角度
      ctx.font = "14px Arial";
      ctx.fillStyle = "rgba(50, 50, 50, 0.9)";

      // 在Jerry连线上标注角度
      const jerryLabelX =
        poolCenterRef.current.x + 0.3 * (jerry.x - poolCenterRef.current.x);
      const jerryLabelY =
        poolCenterRef.current.y + 0.3 * (jerry.y - poolCenterRef.current.y);
      ctx.fillText(`${jerryAngle.toFixed(1)}°`, jerryLabelX, jerryLabelY);

      // 在Tom连线上标注角度
      const tomLabelX =
        poolCenterRef.current.x + 0.3 * (tom.x - poolCenterRef.current.x);
      const tomLabelY =
        poolCenterRef.current.y + 0.3 * (tom.y - poolCenterRef.current.y);
      ctx.fillText(`${tomAngle.toFixed(1)}°`, tomLabelX, tomLabelY);

      // 标注夹角
      ctx.fillStyle = "rgba(100, 0, 150, 0.9)"; // 紫色
      const centerLabelX = poolCenterRef.current.x + 30;
      const centerLabelY = poolCenterRef.current.y - 10;
      ctx.fillText(
        `夹角: ${angleDiff.toFixed(1)}°`,
        centerLabelX,
        centerLabelY
      );
    }

    // 绘制轨迹
    if (showTrails) {
      // Jerry的轨迹
      ctx.beginPath();
      if (jerryTrail.length > 0) {
        ctx.moveTo(jerryTrail[0].x, jerryTrail[0].y);
        for (let i = 1; i < jerryTrail.length; i++) {
          ctx.lineTo(jerryTrail[i].x, jerryTrail[i].y);
        }
      }
      ctx.strokeStyle = "rgba(150, 150, 150, 0.6)";
      ctx.lineWidth = 1;
      ctx.stroke();

      // Tom的轨迹
      ctx.beginPath();
      if (tomTrail.length > 0) {
        ctx.moveTo(tomTrail[0].x, tomTrail[0].y);
        for (let i = 1; i < tomTrail.length; i++) {
          ctx.lineTo(tomTrail[i].x, tomTrail[i].y);
        }
      }
      ctx.strokeStyle = "rgba(255, 100, 100, 0.6)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // 绘制Jerry（老鼠）- 使用更卡通的样式，但更小
    ctx.beginPath();
    ctx.arc(jerry.x, jerry.y, 7, 0, 2 * Math.PI); // 从10减小到7
    ctx.fillStyle = "#A0A0A0"; // 灰色
    ctx.fill();

    // 添加老鼠耳朵 - 缩小
    ctx.beginPath();
    ctx.arc(jerry.x - 4, jerry.y - 5, 3, 0, 2 * Math.PI); // 从5减小到3
    ctx.arc(jerry.x + 4, jerry.y - 5, 3, 0, 2 * Math.PI); // 从5减小到3
    ctx.fillStyle = "#A0A0A0";
    ctx.fill();

    // 在老鼠上方绘制标签
    ctx.font = "16px Arial";
    ctx.fillStyle = "#333333";
    ctx.fillText("杰瑞鼠", jerry.x - 25, jerry.y - 15);

    // 如果是定向游泳，绘制方向指示箭头
    if (strategy === "directedSwim") {
      const angle = (direction * Math.PI) / 180;
      const arrowLength = 20;
      ctx.beginPath();
      ctx.moveTo(jerry.x, jerry.y);
      ctx.lineTo(
        jerry.x + Math.cos(angle) * arrowLength,
        jerry.y + Math.sin(angle) * arrowLength
      );
      ctx.strokeStyle = "#009900";
      ctx.lineWidth = 3;
      ctx.stroke();

      // 箭头头部
      ctx.beginPath();
      ctx.moveTo(
        jerry.x + Math.cos(angle) * arrowLength,
        jerry.y + Math.sin(angle) * arrowLength
      );
      ctx.lineTo(
        jerry.x + Math.cos(angle + 0.3) * (arrowLength - 5),
        jerry.y + Math.sin(angle + 0.3) * (arrowLength - 5)
      );
      ctx.lineTo(
        jerry.x + Math.cos(angle - 0.3) * (arrowLength - 5),
        jerry.y + Math.sin(angle - 0.3) * (arrowLength - 5)
      );
      ctx.closePath();
      ctx.fillStyle = "#009900";
      ctx.fill();
    }

    // 绘制Tom（猫）- 使用更卡通的样式，但更小
    ctx.beginPath();
    ctx.arc(tom.x, tom.y, 10, 0, 2 * Math.PI); // 从15减小到10
    ctx.fillStyle = "#FF6666"; // 橘红色
    ctx.fill();

    // 添加猫耳朵 - 缩小
    ctx.beginPath();
    // 缩小耳朵尺寸
    ctx.moveTo(tom.x - 6, tom.y - 6); // 从8减小到6
    ctx.lineTo(tom.x - 10, tom.y - 14); // 从15/20减小到10/14
    ctx.lineTo(tom.x, tom.y - 8); // 从10减小到8
    ctx.closePath();
    ctx.moveTo(tom.x + 6, tom.y - 6); // 从8减小到6
    ctx.lineTo(tom.x + 10, tom.y - 14); // 从15/20减小到10/14
    ctx.lineTo(tom.x, tom.y - 8); // 从10减小到8
    ctx.closePath();
    ctx.fillStyle = "#FF6666";
    ctx.fill();

    // 在猫上方绘制标签
    ctx.font = "16px Arial";
    ctx.fillStyle = "#CC0000";
    ctx.fillText("汤姆猫", tom.x - 25, tom.y - 20);

    // 如果Tom抓住了Jerry，绘制抓住效果
    if (captureEffect) {
      ctx.beginPath();
      ctx.arc(jerry.x, jerry.y, 40, 0, 2 * Math.PI);
      ctx.fillStyle = "rgba(255, 255, 0, 0.3)";
      ctx.fill();
      ctx.strokeStyle = "rgba(255, 165, 0, 0.8)";
      ctx.lineWidth = 3;
      ctx.stroke();

      // 绘制星星效果
      for (let i = 0; i < 8; i++) {
        const angle = (i * Math.PI) / 4;
        const radius = 40;
        const x = jerry.x + Math.cos(angle) * radius;
        const y = jerry.y + Math.sin(angle) * radius;

        ctx.beginPath();
        for (let j = 0; j < 5; j++) {
          const starAngle = (j * Math.PI * 2) / 5 - Math.PI / 2;
          const starX = x + Math.cos(starAngle) * (j % 2 === 0 ? 6 : 3);
          const starY = y + Math.sin(starAngle) * (j % 2 === 0 ? 6 : 3);
          if (j === 0) {
            ctx.moveTo(starX, starY);
          } else {
            ctx.lineTo(starX, starY);
          }
        }
        ctx.closePath();
        ctx.fillStyle = "rgba(255, 255, 0, 0.8)";
        ctx.fill();
      }
    }

    // 绘制状态信息文字
    ctx.font = "20px Arial";

    if (hasEscaped) {
      ctx.fillStyle = "#00AA00";
      ctx.fillText("杰瑞鼠成功逃跑啦！太棒了！", 20, 30);
    } else if (hasCaught) {
      ctx.fillStyle = "#CC0000";
      ctx.fillText("汤姆猫抓住了杰瑞鼠！哎呀！", 20, 30);
    }

    ctx.fillStyle = "#333333";
    ctx.font = "18px Arial";
    ctx.fillText(`⏱️ 时间：${elapsedTime.toFixed(1)}秒 / ${maxTime}秒`, 20, 60);

    // 显示实时距离
    const currentDistance = distance(jerry, tom) / SCALE_FACTOR;
    ctx.fillText(`猫鼠距离：${currentDistance.toFixed(2)}米`, 20, 90);

    // 显示老鼠到边缘的距离
    const distToEdge =
      (POOL_RADIUS - distance(jerry, poolCenterRef.current)) / SCALE_FACTOR;
    ctx.fillText(`到岸距离：${distToEdge.toFixed(2)}米`, 20, 120);

    // 如果是绕圈策略，显示圈半径
    if (strategy === "circleSwim" && circleSwimRadius) {
      const displayRadius = (circleSwimRadius / SCALE_FACTOR).toFixed(2);
      // 检查是否为有效数字
      if (!isNaN(parseFloat(displayRadius))) {
        ctx.fillText(`⭕ 绕圈半径：${displayRadius}米`, 20, 150);
      } else {
        ctx.fillText(`⭕ 绕圈半径：计算中...`, 20, 150);
      }
    }
  };

  // 更新游戏状态
  const updateSimulation = () => {
    if (!isRunning) return;

    // 到达设定时间后自动暂停
    if (elapsedTime >= maxTime) {
      setIsRunning(false);
      return;
    }

    // Update time，使用固定的时间步长，模拟速度只影响执行频率而不影响计算精度
    setElapsedTime((prev) => prev + timeStep);

    // Calculate new Jerry position based on strategy
    let newJerryX = jerry.x;
    let newJerryY = jerry.y;
    let jerryAngle = 0;

    switch (strategy) {
      case "directedSwim":
        // 向指定方向游泳
        jerryAngle = (direction * Math.PI) / 180;
        newJerryX =
          jerry.x + Math.cos(jerryAngle) * JERRY_SPEED * SIMULATION_SPEED;
        newJerryY =
          jerry.y + Math.sin(jerryAngle) * JERRY_SPEED * SIMULATION_SPEED;
        break;

      case "avoidTom":
        // 计算远离Tom的角度 - 从Jerry到Tom的方向
        const avoidAngle = angleBetween(jerry, tom);
        // 向相反方向游泳（反方向）
        jerryAngle = avoidAngle + Math.PI;

        newJerryX =
          jerry.x + Math.cos(jerryAngle) * JERRY_SPEED * SIMULATION_SPEED;
        newJerryY =
          jerry.y + Math.sin(jerryAngle) * JERRY_SPEED * SIMULATION_SPEED;
        break;

      case "circleSwim":
        // 如果半径尚未设置，计算当前Jerry与圆心的距离作为初始半径
        if (circleSwimRadius === null) {
          const initialRadius = distance(jerry, circlePoint);
          if (initialRadius > 20) {
            // 只有当Jerry不在圆心附近时才设置半径
            setCircleSwimRadius(initialRadius);
            // 初始化角度为从圆心到Jerry的角度
            setCircleSwimAngle(angleBetween(circlePoint, jerry));
          } else {
            // Jerry在圆心附近，暂停游戏并提醒
            setIsRunning(false);
            return;
          }
        }

        // 确保半径大于0，防止除以零错误
        if (circleSwimRadius > 0) {
          // 计算角速度（速度/半径），固定的物理计算精度
          const angularSpeed =
            (JERRY_SPEED * SIMULATION_SPEED) / circleSwimRadius;

          // 根据方向更新角度
          const newCircleAngle =
            circleSwimAngle +
            (circleDirection === "clockwise" ? -angularSpeed : angularSpeed);

          setCircleSwimAngle(newCircleAngle);

          // 根据新的角度和固定半径计算新位置
          newJerryX =
            circlePoint.x + Math.cos(newCircleAngle) * circleSwimRadius;
          newJerryY =
            circlePoint.y + Math.sin(newCircleAngle) * circleSwimRadius;
        } else {
          // 如果半径为0或负数（极少情况），重置为默认值
          const defaultRadius = 50; // 设置一个合理的默认半径（像素）
          setCircleSwimRadius(defaultRadius);
        }
        break;
    }

    // 检查Jerry是否仍在池中
    const distToCenter = distance(
      { x: newJerryX, y: newJerryY },
      poolCenterRef.current
    );

    const isAtEdge = Math.abs(distToCenter - POOL_RADIUS) < 5; // 考虑一点边缘误差

    if (distToCenter > POOL_RADIUS) {
      // Jerry到达边缘！
      // 在边缘检查是否与Tom的距离足够近，如果是则被抓住
      const tomJerryDist = distance(tom, { x: newJerryX, y: newJerryY });
      if (tomJerryDist / SCALE_FACTOR < 0.1) {
        // 如果实际距离小于0.1个单位
        setHasCaught(true);
        setCaptureEffect(true);
        setIsRunning(false);

        // 2秒后关闭抓住效果
        setTimeout(() => {
          setCaptureEffect(false);
        }, 2000);
      } else {
        // 没被抓住，成功逃脱
        setHasEscaped(true);
        setIsRunning(false);
      }
    } else {
      // 更新Jerry的位置
      setJerry({ x: newJerryX, y: newJerryY });
      setJerryTrail((prev) => [...prev, { x: newJerryX, y: newJerryY }]);

      // 使用优化后的Tom移动策略计算新位置
      const newTomPosition = calculateTomPosition(jerry, tom);

      // 更新Tom的位置
      setTom(newTomPosition);
      setTomTrail((prev) => [...prev, newTomPosition]);

      // 检查Tom是否抓住了Jerry（在Jerry没到边缘时）
      const tomJerryDist = distance(newTomPosition, {
        x: newJerryX,
        y: newJerryY,
      });
      if (tomJerryDist / SCALE_FACTOR < 0.1) {
        // 如果实际距离小于0.1个单位
        setHasCaught(true);
        setCaptureEffect(true);
        setIsRunning(false);

        // 2秒后关闭抓住效果
        setTimeout(() => {
          setCaptureEffect(false);
        }, 2000);
      }
    }
  };

  // 动画循环
  useEffect(() => {
    if (isRunning) {
      // 根据模拟速度调整动画更新的频率，而不是计算步长
      // 这样保证了在任何速度下的计算精度都是一致的
      const interval = setInterval(
        updateSimulation,
        100 / simulationSpeedFactor
      );
      return () => clearInterval(interval);
    }
  }, [
    isRunning,
    jerry,
    tom,
    strategy,
    direction,
    reactionPrecision,
    circlePoint,
    circleDirection,
    circleSwimRadius,
    circleSwimAngle,
    simulationSpeedFactor,
  ]);

  // 绘图循环
  useEffect(() => {
    const interval = setInterval(drawSimulation, 16); // ~60fps
    return () => clearInterval(interval);
  }, [
    jerry,
    tom,
    jerryTrail,
    tomTrail,
    strategy,
    direction,
    circlePoint,
    showTrails,
    hasEscaped,
    hasCaught,
    elapsedTime,
    captureEffect,
    circleSwimRadius,
  ]);

  // 渲染策略控制界面
  const renderStrategyControls = () => {
    switch (strategy) {
      case "directedSwim":
        return (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center flex-wrap">
              <label className="font-medium text-base sm:text-lg">
                🧭 游泳方向：
              </label>
              <input
                type="range"
                min="0"
                max="359.9"
                step="0.1"
                value={direction}
                onChange={(e) => setDirection(parseFloat(e.target.value))}
                className="w-24 sm:w-32 h-6"
                disabled={isRunning}
              />
              <input
                type="number"
                min="0"
                max="359.9"
                step="0.1"
                value={direction}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 0 && val < 360) {
                    setDirection(val);
                  }
                }}
                className="w-16 p-1 border rounded text-center font-bold"
                disabled={isRunning}
              />
              <span className="text-base sm:text-lg font-bold">
                {direction.toFixed(1)}°
              </span>
            </div>
            <div className="text-sm sm:text-base text-blue-600">
              小提示：拖动滑块或直接输入角度数字！
            </div>
          </div>
        );

      case "avoidTom":
        return (
          <div className="flex flex-col gap-2">
            <div className="text-sm sm:text-base text-blue-600">
              杰瑞鼠会实时调整方向，始终逃离汤姆猫！
            </div>
          </div>
        );

      case "circleSwim":
        return (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center flex-wrap">
              <label className="font-medium text-base sm:text-lg">
                ⭕ 绕圈模式：
              </label>
              <div className="text-sm sm:text-base font-bold text-purple-600">
                围绕游泳池圆心绕圈游泳
              </div>
            </div>

            {/* 提醒信息 */}
            {isJerryAtCenter() && (
              <div className="bg-yellow-100 border-l-4 border-yellow-500 p-3 rounded">
                <div className="flex items-center">
                  <div className="text-yellow-700 text-sm sm:text-base">
                    ⚠️ <strong>提醒：</strong>
                    杰瑞鼠还在游泳池圆心，请先用其他策略让杰瑞鼠游到外面一些，再开始绕圈！
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2 items-center flex-wrap">
              <label className="font-medium text-base sm:text-lg">
                🔄 绕圈方向：
              </label>
              <select
                value={circleDirection}
                onChange={(e) => setCircleDirection(e.target.value)}
                className="p-1 border rounded text-base sm:text-lg font-medium bg-blue-50"
                disabled={isRunning}
              >
                <option value="clockwise">顺时针</option>
                <option value="counterclockwise">逆时针</option>
              </select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 p-2 sm:p-4 bg-gradient-to-b from-blue-50 to-cyan-50 min-h-screen">
      <h1 className="text-xl sm:text-3xl font-bold text-blue-700 mb-2 bg-yellow-100 px-3 sm:px-6 py-2 rounded-lg shadow-md text-center">
        🐱 汤姆猫抓老鼠 🐭 - 你能逃跑吗？
      </h1>

      <div className="relative">
        <canvas
          ref={canvasRef}
          width="500"
          height="500"
          className="border-4 border-blue-400 bg-blue-100 shadow-lg rounded-lg max-w-full h-auto"
          style={{ maxWidth: "90vw", maxHeight: "90vw" }}
        />

        {hasEscaped && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-100 p-4 rounded-lg shadow-lg border-4 border-green-400 animate-bounce max-w-xs text-center">
            <h2 className="text-xl sm:text-3xl font-bold text-green-600">
              🎉 太棒了！
            </h2>
            <p className="text-base sm:text-xl text-green-700">
              杰瑞鼠成功逃跑了！
            </p>
          </div>
        )}

        {hasCaught && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-100 p-4 rounded-lg shadow-lg border-4 border-red-400 max-w-xs text-center">
            <h2 className="text-xl sm:text-3xl font-bold text-red-600">
              😮 哎呀！
            </h2>
            <p className="text-base sm:text-xl text-red-700">
              汤姆猫抓住了杰瑞鼠！
            </p>
          </div>
        )}
      </div>

      {/* 游戏控制区域 - 响应式设计 */}
      <div className="w-full max-w-4xl bg-white p-3 sm:p-4 rounded-lg shadow-md border-2 border-blue-300">
        {/* 主要游戏控制按钮 - 响应式 */}
        <div className="flex justify-center mb-4 gap-3 sm:gap-6 flex-wrap">
          <button
            onClick={() => setIsRunning(!isRunning)}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full text-base sm:text-xl flex items-center gap-2 shadow-md transform hover:scale-105 transition ${
              isRunning
                ? "bg-orange-500 hover:bg-orange-600"
                : "bg-green-500 hover:bg-green-600"
            } text-white font-bold ${
              isJerryAtCenter() && strategy === "circleSwim"
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            disabled={
              hasEscaped ||
              hasCaught ||
              (isJerryAtCenter() && strategy === "circleSwim")
            }
          >
            {isRunning ? <Pause size={24} /> : <Play size={24} />}
            {isRunning ? "暂停游戏" : "开始游戏"}
          </button>

          <button
            onClick={resetSimulation}
            className="px-4 sm:px-6 py-2 sm:py-3 rounded-full text-base sm:text-xl bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-2 font-bold shadow-md transform hover:scale-105 transition"
          >
            <RotateCcw size={24} />
            重新开始
          </button>
        </div>

        {/* 游戏设置面板 - 响应式布局 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <label className="font-bold text-base sm:text-lg">
                ⏱️ 游戏时间：
              </label>
              <input
                type="number"
                min="10"
                max="300"
                step="10"
                value={maxTime}
                onChange={(e) => setMaxTime(parseFloat(e.target.value))}
                className="w-16 sm:w-20 p-1 sm:p-2 border-2 border-blue-300 rounded text-center text-base sm:text-lg font-bold"
                disabled={isRunning}
              />
              <span className="text-base sm:text-lg">秒</span>
            </div>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <label className="font-bold text-base sm:text-lg">
                ⚡ 游戏速度：
              </label>
              <select
                value={simulationSpeedFactor}
                onChange={(e) =>
                  setSimulationSpeedFactor(parseFloat(e.target.value))
                }
                className="p-1 sm:p-2 border-2 border-blue-300 rounded text-base sm:text-lg font-bold bg-white"
                disabled={isRunning}
              >
                <option value="0.5">慢一点 (0.5倍)</option>
                <option value="1">正常 (1倍)</option>
                <option value="2">快一点 (2倍)</option>
                <option value="5">超快 (5倍)</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 sm:gap-6 mb-4 flex-wrap">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={showTrails}
              onChange={(e) => setShowTrails(e.target.checked)}
              id="show-trails"
              className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
            />
            <label htmlFor="show-trails" className="text-sm sm:text-lg">
              显示移动轨迹
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={showGuideLines}
              onChange={(e) => setShowGuideLines(e.target.checked)}
              id="show-guide-lines"
              className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
            />
            <label htmlFor="show-guide-lines" className="text-sm sm:text-lg">
              显示辅助线
            </label>
          </div>
        </div>

        {/* 游戏策略选择区 - 响应式设计 */}
        <div className="bg-yellow-50 p-3 sm:p-4 rounded-lg border-2 border-yellow-200 mb-4">
          <div className="mb-2">
            <label className="font-bold text-lg sm:text-xl text-purple-700">
              🐭杰瑞鼠的游泳方法：
            </label>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-4 justify-center mt-2">
            <button
              onClick={() => setStrategy("directedSwim")}
              className={`px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center gap-2 text-sm sm:text-lg font-bold ${
                strategy === "directedSwim"
                  ? "bg-blue-500 text-white shadow-md transform scale-105"
                  : "bg-blue-100 hover:bg-blue-200 text-blue-800"
              } transition`}
              disabled={isRunning}
            >
              <ArrowUp size={20} />
              直线游泳
            </button>

            <button
              onClick={() => setStrategy("avoidTom")}
              className={`px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center gap-2 text-sm sm:text-lg font-bold ${
                strategy === "avoidTom"
                  ? "bg-green-500 text-white shadow-md transform scale-105"
                  : "bg-green-100 hover:bg-green-200 text-green-800"
              } transition`}
              disabled={isRunning}
            >
              <ArrowLeftRight size={20} />
              躲避汤姆
            </button>

            <button
              onClick={() => setStrategy("circleSwim")}
              className={`px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center gap-2 text-sm sm:text-lg font-bold ${
                strategy === "circleSwim"
                  ? "bg-purple-500 text-white shadow-md transform scale-105"
                  : "bg-purple-100 hover:bg-purple-200 text-purple-800"
              } transition`}
              disabled={isRunning}
            >
              <RefreshCw size={20} />
              绕圈游泳
            </button>
          </div>

          <div className="mt-4">{renderStrategyControls()}</div>
        </div>
      </div>

      {/* 游戏规则面板 - 响应式设计 */}
      <div className="bg-white p-3 sm:p-4 rounded-lg w-full max-w-4xl shadow-md border-2 border-green-300 mt-2">
        <h3 className="font-bold mb-3 text-lg sm:text-xl text-green-600 border-b-2 border-green-200 pb-2">
          🎮 游戏规则：
        </h3>
        <ul className="list-none pl-2 space-y-2">
          <li className="flex items-start">
            <span className="text-blue-600 text-xl sm:text-2xl mr-2 mt-1">
              💧
            </span>
            <span className="text-sm sm:text-base">
              游泳池半径有
              <span className="text-red-500 font-bold">10米</span>
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 text-xl sm:text-2xl mr-2 mt-1">
              🐭
            </span>
            <span className="text-sm sm:text-base">
              杰瑞鼠游泳速度：
              <span className="text-red-500 font-bold">每秒1米</span>
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 text-xl sm:text-2xl mr-2 mt-1">
              🐱
            </span>
            <span className="text-sm sm:text-base">
              汤姆猫跑步速度：
              <span className="text-red-500 font-bold">每秒4米</span>
              （是杰瑞鼠的4倍快！）
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 text-xl sm:text-2xl mr-2 mt-1">
              🎯
            </span>
            <span className="text-sm sm:text-base">
              当猫和老鼠距离小于
              <span className="text-red-500 font-bold">0.1米</span>
              时，汤姆猫就能抓住杰瑞鼠
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 text-xl sm:text-2xl mr-2 mt-1">
              🏆
            </span>
            <span className="text-sm sm:text-base">
              杰瑞鼠到达池边，且与汤姆猫保持安全距离时就能成功逃脱！
            </span>
          </li>
        </ul>

        <h3 className="font-bold mt-4 mb-3 text-lg sm:text-xl text-green-600 border-b-2 border-green-200 pb-2">
          🏊游泳方法说明：
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="font-bold text-blue-700 mb-1 flex items-center text-sm sm:text-base">
              <ArrowUp size={16} className="mr-1" /> 直线游泳
            </div>
            <div className="text-sm">
              杰瑞鼠笔直向前游，你可以调整游泳的方向角度哦！
            </div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="font-bold text-green-700 mb-1 flex items-center text-sm sm:text-base">
              <ArrowLeftRight size={16} className="mr-1" /> 躲避汤姆
            </div>
            <div className="text-sm">杰瑞鼠总是游向远离汤姆猫的方向！</div>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg">
            <div className="font-bold text-purple-700 mb-1 flex items-center text-sm sm:text-base">
              <RefreshCw size={16} className="mr-1" /> 绕圈游泳
            </div>
            <div className="text-sm">
              杰瑞鼠围绕游泳池圆心转圈游，Jerry在圆心时需要先移动到外面！
            </div>
          </div>
        </div>

        <p className="mt-4 text-gray-700 bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400 text-sm sm:text-base">
          <span className="font-bold">🐱汤姆猫很聪明：</span>
          它会一直追着杰瑞鼠跑，找到最佳位置来靠近杰瑞鼠。如果杰瑞鼠朝它游来，它会停在原地等着；只有当杰瑞鼠试图逃跑时，它才会追击！
        </p>
      </div>

      <footer className="mt-6 w-full text-center text-xs sm:text-sm text-gray-600 px-2">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利
      </footer>
    </div>
  );
};

export default TomAndJerrySimulation;
